import { CONTACT, SITE } from '@/constants/site.constants';
import type { LegalDocumentContent } from '@/types/legal.types';

/**
 * Published child safety standards, required by Google Play for apps in the Social
 * category. Play checks three things: the page loads, it addresses CSAE, and it names
 * the app as it appears on the store listing — hence SITE.name throughout.
 *
 * Every claim here describes something the platform actually does. Do not add a
 * commitment without the code to back it: this page is the public standard Shoutt is
 * held to, and Play's designated contact must be able to speak to it.
 */
export const CHILD_SAFETY_CONTENT: LegalDocumentContent = {
  title: `${SITE.name} Child Safety Standards`,
  summary: `${SITE.name} has zero tolerance for child sexual abuse and exploitation (CSAE). This page sets out the standards we hold ourselves to, how we prevent CSAE on the platform, how to report it, and who to contact.`,
  sections: [
    {
      id: 'commitment',
      heading: '1. Our commitment',
      paragraphs: [
        `${SITE.name} prohibits child sexual abuse material (CSAM) and any content or conduct that sexualises, exploits, endangers, or otherwise harms a child. This prohibition is absolute and applies to every part of the platform: listings, Shoutts, profiles, photographs, videos, reviews, and private messages.`,
        'We comply with applicable child protection law in the jurisdictions in which we operate, including the Protection of Children from Sexual Offences (POCSO) Act, 2012 and the Information Technology Act, 2000 and rules made under it.',
      ],
    },
    {
      id: 'adults-only',
      heading: '2. The platform is for adults',
      paragraphs: [
        `${SITE.name} is not directed at children. Our Terms require every user to be at least 18 years of age and legally competent to enter into a binding contract.`,
        'Accounts are created only with a verified college email address at a campus we have opened, and each account is bound to that campus. We do not knowingly permit accounts for anyone under 18. Where we learn that an account holder is a child, we remove the account.',
      ],
    },
    {
      id: 'prevention',
      heading: '3. How we prevent CSAE',
      bullets: [
        'Verified accounts. Every account is tied to a verified college email address and carries a real name, department and year rather than an anonymous handle.',
        'Automated screening. Listings and Shoutts are screened automatically for sexual and other prohibited content before and after they appear. Flagged items are withheld or surfaced to a human review queue.',
        'Human review. Content our automated screening flags, and anything a user reports, is reviewed by a member of the Shoutt team.',
        'Enforcement. We remove violating content and suspend, restrict or permanently ban the accounts responsible.',
        'Contained surface. Users see only their own campus, and messaging is scoped to a booking rather than open to the general public.',
      ],
    },
    {
      id: 'reporting',
      heading: '4. How to report CSAE',
      paragraphs: [
        `Any user can report a listing, a Shoutt, or another user from inside the ${SITE.name} app. Reports go to our moderation queue and are reviewed by a person.`,
        `To report suspected CSAE or CSAM directly, including if you are not a ${SITE.name} user, email ${CONTACT.childSafetyEmail}. Reports of child safety violations are prioritised above all other reports.`,
        'If a child is in immediate danger, contact your local police first. In India you can also reach the national child helpline on 1098, or report online at the National Cyber Crime Reporting Portal (cybercrime.gov.in).',
      ],
    },
    {
      id: 'response',
      heading: '5. What we do when CSAE is reported',
      bullets: [
        'We remove the content from the platform.',
        'We suspend the account pending review, and permanently ban confirmed offenders.',
        'We preserve the relevant records where we are required or permitted to do so, so that they are available to law enforcement.',
        'We report apparent child sexual abuse material to the appropriate authorities as required by applicable law.',
      ],
    },
    {
      id: 'contact',
      heading: '6. Point of contact',
      paragraphs: [
        `Child safety and CSAE enquiries, including from regulators, law enforcement and platform partners: ${CONTACT.childSafetyEmail}.`,
        `General support: ${CONTACT.email}.`,
      ],
    },
  ],
};
